import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Mail, Lock, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";

export default function AdminLogin() {
  const { setUser, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
      if (data.role !== "admin") {
        await logout();
        setError(t("notAdmin"));
        return;
      }
      setUser(data);
      nav("/admin/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid-noise flex flex-col items-center justify-center px-4 py-10">
      <div className="mb-6 flex items-center gap-3">
        <div className="grid place-items-center h-12 w-12 rounded-2xl bg-amber-500/15 ring-1 ring-amber-500/40 text-amber-300">
          <Shield size={24} />
        </div>
        <div>
          <span className="font-display text-2xl font-extrabold gold-text">{t("brand")}</span>
          <p className="text-xs text-amber-300/80 tracking-widest uppercase">{t("adminPanel")}</p>
        </div>
      </div>

      <div className="mw-card w-full max-w-sm p-6 sm:p-8 relative z-10 border-amber-500/30">
        <h1 className="font-display text-2xl font-bold mb-1">{t("adminLogin")}</h1>
        <p className="text-sm text-slate-400 mb-6">{t("adminOnly")}</p>

        <form onSubmit={submit} className="space-y-3">
          <div className="flex items-center gap-2 rounded-xl border border-amber-500/25 bg-black/40 px-3">
            <Mail size={17} className="text-amber-400/70" />
            <input required type="email" placeholder={t("email")} value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="admin-email-input" className="w-full bg-transparent py-2.5 text-sm focus:outline-none" />
          </div>
          <div className="flex items-center gap-2 rounded-xl border border-amber-500/25 bg-black/40 px-3">
            <Lock size={17} className="text-amber-400/70" />
            <input required type="password" placeholder={t("password")} value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="admin-password-input" className="w-full bg-transparent py-2.5 text-sm focus:outline-none" />
          </div>
          {error && <p className="text-sm text-rose-400" data-testid="admin-auth-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="admin-login-submit"
            className="w-full btn-gold rounded-xl py-2.5 flex items-center justify-center gap-2">
            {busy && <Loader2 size={17} className="animate-spin" />} {t("login")}
          </button>
        </form>
      </div>
    </div>
  );
}
